import { memoize } from 'es-toolkit/compat';
import { faker } from '@faker-js/faker';

export type ServiceInfo = {
    name: string;
    version: string;
};

export type CapacityStatus = 'freeCapacity' | 'vacation' | 'wellBooked' | 'fullyBooked';
export type CapacityState = 'success' | 'warning' | 'danger';

export type UserCapacity = {
    percent: number;
    status: CapacityStatus;
};

export type AppointmentLocation = {
    id: string;
    city: string;
    lat: number;
    lng: number;
};

export type AppointmentPayload = {
    nextAppointment: string;
    topic: string;
    location: AppointmentLocation;
};

export type User = {
    itemId: string;
    userAvatar: string;
    capacityState: CapacityState;
    bookingSlotsBooked: number;
    bookingSlotsTotal: number;
    nextAppointment: string | null;
    nextAppointmentTopic: string | null;
    nextAppointmentLocation: AppointmentLocation | null;
    userNickName: string;
    userFirstName: string;
    userLastName: string;
    userCapacity: UserCapacity;
    salesArea: string;
    userEmail: string;
    userPhoneNumber: string;
};

export type TransformedUser = User & {
    userCapacityLabel: string;
    userCapacityValue: number;
    freeSlotsValue: number;
    dataKey: string;
};

export type ColumnKey =
    | 'itemId'
    | 'employee'
    | 'userNickName'
    | 'userFirstName'
    | 'userLastName'
    | 'userCapacity'
    | 'freeSlots'
    | 'nextAppointment'
    | 'contactData'
    | 'salesArea'
    | 'userEmail'
    | 'userPhoneNumber';

export type ColumnDetails = {
    resizeMaxWidth?: number;
    resizeMinWidth?: number;
    width?: number | string;
};

export type ColumnDetailsMap = Record<string, ColumnDetails>;

export type ColumnsConfig = {
    columnOrder: ColumnKey[];
    hiddenColumns: ColumnKey[];
    columnsDetails: ColumnDetailsMap;
};

export type TableConfig = {
    defaultColumnOrder: ColumnKey[];
    defaultHiddenColumns: ColumnKey[];
    defaultColumnsDetails: ColumnDetailsMap;
    columnLabels: Record<ColumnKey, string>;
    sortKeyReplacement: Partial<Record<ColumnKey, keyof TransformedUser>>;
    defaultSortKey: ColumnKey;
    TABLE_CHUNK_SIZE: number;
};

export const serviceInfo: ServiceInfo = {
    name: 'Demo service',
    version: '0.0.1',
};

export const BASE_PATH: string = '/demoService';

export const capacityStatuses: CapacityStatus[] = ['freeCapacity', 'vacation', 'wellBooked', 'fullyBooked'];
export const capacityStatusLabels: Record<CapacityStatus, string> = {
    freeCapacity: 'Free capacity',
    vacation: 'Vacation',
    wellBooked: 'Well booked',
    fullyBooked: 'Fully booked',
};
const DEMO_ROW_COUNT = 250;
export const BOOKING_SLOT_COUNT = 10;
export const appointmentLocations: AppointmentLocation[] = [
    { id: 'berlin', city: 'Berlin', lat: 52.52, lng: 13.405 },
    { id: 'hamburg', city: 'Hamburg', lat: 53.5511, lng: 9.9937 },
    { id: 'munich', city: 'Munich', lat: 48.1351, lng: 11.582 },
    { id: 'cologne', city: 'Cologne', lat: 50.9375, lng: 6.9603 },
    { id: 'frankfurt', city: 'Frankfurt', lat: 50.1109, lng: 8.6821 },
    { id: 'stuttgart', city: 'Stuttgart', lat: 48.7758, lng: 9.1829 },
    { id: 'dusseldorf', city: 'Dusseldorf', lat: 51.2277, lng: 6.7735 },
    { id: 'leipzig', city: 'Leipzig', lat: 51.3397, lng: 12.3731 },
];
const salesAreas = ['Truck', 'Bus', 'Van', 'Digital services'];

const getInitialBookingSlotsBooked = (index: number) => {
    const segment = index % 100;

    if (segment < 20 || segment >= 90) {
        return 0;
    }

    if (segment < 45) {
        return 1 + (index % 3);
    }

    if (segment < 75) {
        return 4 + (index % 4);
    }

    return 7 + (index % 4);
};

export const getUtilizationFromBookingSlots = (bookingSlotsBooked: number, bookingSlotsTotal: number) =>
    Math.round((bookingSlotsBooked / bookingSlotsTotal) * 100);

export const getCapacityStatusFromBookingSlots = (bookingSlotsBooked: number): CapacityStatus => {
    if (bookingSlotsBooked >= 9) {
        return 'fullyBooked';
    }

    if (bookingSlotsBooked >= 7) {
        return 'wellBooked';
    }

    return 'freeCapacity';
};

const getInitialCapacityStatus = (bookingSlotsBooked: number, index: number) => {
    if (bookingSlotsBooked === 0 && index % 100 >= 90) {
        return 'vacation';
    }

    return getCapacityStatusFromBookingSlots(bookingSlotsBooked);
};

export const getCapacityStateFromCapacityStatus = (capacityStatus: CapacityStatus): CapacityState => {
    switch (capacityStatus) {
        case 'freeCapacity':
            return 'success';
        case 'wellBooked':
        case 'vacation':
            return 'warning';
        case 'fullyBooked':
            return 'danger';
        default:
            return 'success';
    }
};

export const getUserCapacityLabel = (
    capacityStatus: CapacityStatus,
    bookingSlotsBooked: number,
    bookingSlotsTotal: number
) => `${capacityStatusLabels[capacityStatus]} - ${bookingSlotsBooked}/${bookingSlotsTotal} slots booked`;

const formatAppointmentDate = (date: Date) =>
    new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date);

const formatAppointmentTime = (date: Date) =>
    new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);

const getInitialNextAppointment = (index: number, bookingSlotsBooked: number) => {
    if (bookingSlotsBooked === 0) {
        return null;
    }

    const start = new Date();
    start.setDate(start.getDate() + index + 1);
    start.setHours(9 + (index % 6), 0, 0, 0);

    const end = new Date(start);
    end.setHours(start.getHours() + 1);

    return `${formatAppointmentDate(start)}, ${formatAppointmentTime(start)}-${formatAppointmentTime(end)}`;
};

const getInitialAppointmentLocation = (index: number, nextAppointment: string | null) => {
    if (!nextAppointment) {
        return null;
    }

    return appointmentLocations[index % appointmentLocations.length];
};

export const data: User[] = Array.from({ length: DEMO_ROW_COUNT }, (_, index) => {
    const bookingSlotsBooked = getInitialBookingSlotsBooked(index);
    const bookingSlotsTotal = BOOKING_SLOT_COUNT;
    const nextAppointment = getInitialNextAppointment(index, bookingSlotsBooked);
    const nextAppointmentLocation = getInitialAppointmentLocation(index, nextAppointment);
    const capacityStatus = getInitialCapacityStatus(bookingSlotsBooked, index);
    const utilization = getUtilizationFromBookingSlots(bookingSlotsBooked, bookingSlotsTotal);

    return {
        itemId: `${index + 1}`,
        userAvatar: faker.image.personPortrait(),
        capacityState: getCapacityStateFromCapacityStatus(capacityStatus),
        bookingSlotsBooked,
        bookingSlotsTotal,
        nextAppointment,
        nextAppointmentTopic: null,
        nextAppointmentLocation,
        userNickName: faker.internet.username(),
        userFirstName: faker.person.firstName(),
        userLastName: faker.person.lastName(),
        userCapacity: {
            percent: utilization,
            status: capacityStatus,
        },
        salesArea: salesAreas[index % salesAreas.length],
        userEmail: faker.internet.email(),
        userPhoneNumber: faker.phone.number(),
    };
});

// Transform data in order to:
// - Translate item props as we search and sort accordingly with respect to translations or composed values
// - format values as they might be relevant for search
export const transformData: (items: User[]) => TransformedUser[] = memoize((items: User[]) =>
    items.map(item => ({
        ...item,
        userCapacityLabel: getUserCapacityLabel(
            item.userCapacity.status,
            item.bookingSlotsBooked,
            item.bookingSlotsTotal
        ),
        userCapacityValue: item.userCapacity.percent,
        freeSlotsValue: item.bookingSlotsTotal - item.bookingSlotsBooked,
        dataKey: item.itemId,
    }))
);

const defaultColumnOrder: ColumnKey[] = [
    'itemId',
    'employee',
    'salesArea',
    'nextAppointment',
    'userCapacity',
    'freeSlots',
    'contactData',
];

const defaultHiddenColumns: ColumnKey[] = ['itemId'];

const defaultSortKey: ColumnKey = 'nextAppointment';

const columnLabels: Record<ColumnKey, string> = {
    itemId: 'Id',
    employee: 'Sales Employee',
    userNickName: 'Nickname',
    userFirstName: 'First name',
    userLastName: 'Last name',
    userCapacity: 'Utilization',
    freeSlots: 'Free slots',
    nextAppointment: 'Next appointment',
    contactData: 'Contact data',
    salesArea: 'Sales area',
    userEmail: 'Email',
    userPhoneNumber: 'Phone',
};

const defaultColumnsDetails: ColumnDetailsMap = {
    employee: {
        width: 260,
    },
    userCapacity: {
        width: 140,
    },
    freeSlots: {
        width: 110,
    },
    nextAppointment: {
        width: 180,
    },
    salesArea: {
        width: 200,
    },
    // contactData: {
    //     width: 300,
    // },
};

const sortKeyReplacement: Partial<Record<ColumnKey, keyof TransformedUser>> = {
    employee: 'userLastName',
    contactData: 'userEmail',
    userCapacity: 'userCapacityValue',
    freeSlots: 'freeSlotsValue',
};

export const tableConfig: TableConfig = {
    defaultColumnOrder,
    defaultHiddenColumns,
    defaultColumnsDetails,
    columnLabels,
    sortKeyReplacement,
    defaultSortKey,
    TABLE_CHUNK_SIZE: 50,
};
